import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { ShoppingBag, Package, Loader2, RefreshCw, AlertCircle } from 'lucide-react';

// Helper to format cents to dollars
const formatCurrency = (cents) => {
  if (cents === undefined || cents === null) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

function OrdersTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ totalSold: 0, totalRevenue: 0 });

  useEffect(() => {
    loadOrdersData();
  }, []);

  const loadOrdersData = async () => {
    setLoading(true);
    setError(null);

    try {
      // For now, we'll derive orders from commission history
      // In future, a dedicated /api/orders/my-sales endpoint could be created
      const response = await amplifyApi.getMyCommissionHistory(100);

      if (response.success !== false && response.history) {
        // Filter to only 'creator' type commissions (products I sold)
        const creatorCommissions = response.history.filter(c => c.type === 'creator');

        // Group by orderId to get unique orders
        const orderMap = new Map();
        creatorCommissions.forEach(commission => {
          if (commission.orderId && !orderMap.has(commission.orderId)) {
            orderMap.set(commission.orderId, {
              orderId: commission.orderId,
              productId: commission.productId,
              createdAt: commission.createdAt,
              orderAmount: commission.orderAmount,
              commissionAmount: commission.commissionAmount,
              status: commission.status
            });
          }
        });

        const ordersList = Array.from(orderMap.values());
        setOrders(ordersList);

        // Calculate stats
        const totalSold = ordersList.length;
        const totalRevenue = ordersList.reduce((sum, o) => sum + (o.orderAmount || 0), 0);
        setStats({ totalSold, totalRevenue });
      } else {
        setError(response.error || 'Failed to load orders');
      }
    } catch (err) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Loading orders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <button
          onClick={loadOrdersData}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Products Sold */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Package size={16} />
            <span className="text-sm">Products Sold</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.totalSold}</p>
        </div>

        {/* Total Revenue */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <ShoppingBag size={16} />
            <span className="text-sm">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</p>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Sales History</h3>
          <button
            onClick={loadOrdersData}
            className="text-slate-500 hover:text-slate-700 p-1"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">
            <ShoppingBag size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No sales yet</p>
            <p className="text-sm mt-1">
              When someone buys one of your products, you'll see the order details here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Order ID</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Sale Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Your Earnings</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((order, index) => (
                  <tr key={order.orderId || index} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-600">
                        #{order.orderId?.substring(0, 12)}...
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900">
                      {formatCurrency(order.orderAmount)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">
                      +{formatCurrency(order.commissionAmount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          order.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : order.status === 'approved'
                            ? 'bg-blue-100 text-blue-700'
                            : order.status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {order.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-slate-100 rounded-xl p-4 text-sm text-slate-600">
        <p>
          <strong>Note:</strong> Orders shown here are sales of products you created.
          Your 10% creator commission is automatically calculated and added to your pending balance.
        </p>
      </div>
    </div>
  );
}

export default OrdersTab;
